import React from 'react'
import { Tooltip, TooltipProvider } from '../../atoms/Tooltip'
import { DrawingTool, DrawingToolOption, DrawingToolbarAction } from './DrawingTools.types'
import {
  DrawingToolbar,
  DrawingToolbarButton,
  DrawingToolbarLabel,
  DrawingToolbarLabelButton,
} from './DrawingTools.styles'
import {
  Draw3DIcon,
  DrawLineIcon,
  DrawPolygonIcon,
  DrawPrintIcon,
  DrawRulerIcon,
  DrawTrashIcon,
} from './DrawingToolIcons'

const toolbarIcons: Partial<Record<DrawingTool, React.FC>> = {
  measure: DrawRulerIcon,
  line: DrawLineIcon,
  polygon: DrawPolygonIcon,
  '3d': Draw3DIcon,
}

const toolbarOrder: DrawingTool[] = ['measure', 'line', 'polygon', '3d']

interface DrawingToolsToolbarProps {
  tools: DrawingToolOption[]
  activeTool: DrawingTool
  onToolSelect?: (tool: DrawingTool) => void
  onDeleteAll?: () => void
  deleteLabel: string
  hasDrawings: boolean
  onPrint?: () => void
  printLabel: string
  toolbarActions?: DrawingToolbarAction[]
}

export const DrawingToolsToolbar: React.FC<DrawingToolsToolbarProps> = ({
  tools,
  activeTool,
  onToolSelect,
  onDeleteAll,
  deleteLabel,
  hasDrawings,
  onPrint,
  printLabel,
  toolbarActions = [],
}) => {
  const byId = new Map(tools.map((tool) => [tool.id, tool]))
  const modeActive = activeTool !== 'none'
  const showTrash = Boolean(onDeleteAll) && (modeActive || hasDrawings)

  const select = (id: DrawingTool) => {
    onToolSelect?.(id === activeTool ? 'none' : id)
  }

  return (
    <TooltipProvider>
      <DrawingToolbar>
        {toolbarOrder.map((id) => {
          const tool = byId.get(id)
          if (!tool) return null
          const Icon = toolbarIcons[id]
          const active = activeTool === id
          return (
            <Tooltip key={id} content={tool.label} placement="top">
              <DrawingToolbarButton
                type="button"
                $active={active}
                aria-label={tool.label}
                aria-pressed={active}
                disabled={tool.disabled}
                onClick={() => select(id)}
              >
                {Icon ? <Icon /> : tool.icon}
              </DrawingToolbarButton>
            </Tooltip>
          )
        })}
        {showTrash && (
          <Tooltip content={deleteLabel} placement="top">
            <DrawingToolbarButton
              type="button"
              aria-label={deleteLabel}
              disabled={!hasDrawings}
              onClick={onDeleteAll}
            >
              <DrawTrashIcon />
            </DrawingToolbarButton>
          </Tooltip>
        )}
        {toolbarActions.map((action) => {
          const buttonProps = {
            type: 'button' as const,
            'aria-label': action.label,
            'aria-disabled': action.disabled || undefined,
            onClick: action.disabled ? undefined : action.onClick,
          }

          if (action.showLabel) {
            const labelled = (
              <DrawingToolbarLabelButton {...buttonProps}>
                {action.icon}
                <DrawingToolbarLabel>{action.label}</DrawingToolbarLabel>
              </DrawingToolbarLabelButton>
            )
            // The visible label names an enabled action; only a disabled one needs a tooltip.
            return action.disabled ? (
              <Tooltip key={action.id} content={action.tooltip ?? action.label} placement="top">
                {labelled}
              </Tooltip>
            ) : (
              <React.Fragment key={action.id}>{labelled}</React.Fragment>
            )
          }

          return (
            <Tooltip key={action.id} content={action.tooltip ?? action.label} placement="top">
              <DrawingToolbarButton {...buttonProps}>{action.icon}</DrawingToolbarButton>
            </Tooltip>
          )
        })}
        {onPrint && (
          <Tooltip content={printLabel} placement="top">
            <DrawingToolbarButton type="button" aria-label={printLabel} onClick={onPrint}>
              <DrawPrintIcon />
            </DrawingToolbarButton>
          </Tooltip>
        )}
      </DrawingToolbar>
    </TooltipProvider>
  )
}
